import type { SessionSlot } from './types'

/** Ключ занятия для маршрута: группа, дата и время, без личных данных. */
// Разовое занятие может быть без времени: в ключе это "none".
export function sessionKey(groupId: string, date: string, startTime: string): string {
  return `${groupId}~${date}~${startTime ? startTime.replace(':', '') : 'none'}`
}

export function parseSessionKey(key: string): { groupId: string; date: string; startTime: string } | null {
  const parts = key.split('~')
  if (parts.length !== 3 || !/^\d{4}-\d{2}-\d{2}$/.test(parts[1]) || !/^(\d{4}|none)$/.test(parts[2])) {
    return null
  }
  const startTime = parts[2] === 'none' ? '' : `${parts[2].slice(0, 2)}:${parts[2].slice(2)}`
  return { groupId: parts[0], date: parts[1], startTime }
}

/** YYYY-MM-DD в локальном времени устройства. */
export function isoDate(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

export function parseIsoDate(s: string | null | undefined): Date | null {
  if (!s || !/^\d{4}-\d{2}-\d{2}$/.test(s)) return null
  const [y, m, d] = s.split('-').map(Number)
  const date = new Date(y, m - 1, d)
  return isoDate(date) === s ? date : null
}

export function addDays(d: Date, days: number): Date {
  const r = new Date(d)
  r.setDate(r.getDate() + days)
  return r
}

/** Понедельник недели, в которую входит дата. */
export function startOfWeek(d: Date): Date {
  const r = new Date(d.getFullYear(), d.getMonth(), d.getDate())
  const shift = (r.getDay() + 6) % 7
  return addDays(r, -shift)
}

function minutes(hhmm: string): number {
  // Занятие без времени - в начало дня: его не пропустят в «Следом».
  if (!hhmm) return 0
  const [h, m] = hhmm.split(':').map(Number)
  return h * 60 + m
}

function slotEnd(slot: SessionSlot): number {
  // Без времени занятие весь день актуально: «закончилось» про него не скажешь.
  if (!slot.startTime) return 24 * 60
  // Без конца считаем занятие часовым: иначе оно «заканчивается» в момент начала.
  return slot.endTime ? minutes(slot.endTime) : minutes(slot.startTime) + 60
}

export interface TodayPlan {
  /** Идёт сейчас или следующее: главный следующий шаг на экране «Сегодня». */
  focus: SessionSlot | null
  focusState: 'now' | 'next' | null
  /** Остальные будущие занятия дня. */
  later: SessionSlot[]
  /** Закончившиеся сегодня: показываются отдельно, а не как «ближайшая». */
  done: SessionSlot[]
}

/**
 * Раскладывает занятия дня относительно текущего времени. Отменённые в
 * «ближайшую» не попадают, закончившиеся не выдаются за текущие (SC02).
 */
export function planToday(slots: SessionSlot[], now: Date): TodayPlan {
  const today = isoDate(now)
  const nowMin = now.getHours() * 60 + now.getMinutes()
  const todays = slots
    .filter((s) => s.date === today)
    .sort((a, b) => minutes(a.startTime) - minutes(b.startTime))

  const done: SessionSlot[] = []
  const upcoming: SessionSlot[] = []
  for (const s of todays) {
    if (slotEnd(s) <= nowMin) done.push(s)
    else upcoming.push(s)
  }

  const active = upcoming.filter((s) => !s.cancelled)
  const focus = active[0] ?? null
  const focusState = focus ? (focus.startTime && minutes(focus.startTime) <= nowMin ? 'now' : 'next') : null
  const later = upcoming.filter((s) => s !== focus)
  return { focus, focusState, later, done }
}

const WEEKDAYS_SHORT = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб']
const MONTHS_GEN = [
  'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
  'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря',
]
const MONTHS_NOM = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь',
]
const WEEKDAYS_FULL = ['Воскресенье', 'Понедельник', 'Вторник', 'Среда', 'Четверг', 'Пятница', 'Суббота']

export function weekdayShort(d: Date): string {
  return WEEKDAYS_SHORT[d.getDay()]
}

/** «Четверг, 1 октября» */
export function formatDayLong(d: Date): string {
  return `${WEEKDAYS_FULL[d.getDay()]}, ${d.getDate()} ${MONTHS_GEN[d.getMonth()]}`
}

/** «1 октября» */
export function formatDayShort(d: Date): string {
  return `${d.getDate()} ${MONTHS_GEN[d.getMonth()]}`
}

/** «28 сентября - 4 октября» */
export function formatWeekRange(monday: Date): string {
  const sunday = addDays(monday, 6)
  if (monday.getMonth() === sunday.getMonth()) {
    return `${monday.getDate()} - ${sunday.getDate()} ${MONTHS_GEN[sunday.getMonth()]}`
  }
  return `${formatDayShort(monday)} - ${formatDayShort(sunday)}`
}

/** «Октябрь 2026» */
export function formatMonth(d: Date): string {
  return `${MONTHS_NOM[d.getMonth()]} ${d.getFullYear()}`
}

export function formatTimeRange(slot: Pick<SessionSlot, 'startTime' | 'endTime'>): string {
  if (!slot.startTime) return 'Время не указано'
  return slot.endTime ? `${slot.startTime} - ${slot.endTime}` : slot.startTime
}

/** «13 спортсменов» */
export function pluralAthletes(n: number): string {
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod10 === 1 && mod100 !== 11) return `${n} спортсмен`
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return `${n} спортсмена`
  return `${n} спортсменов`
}
